import { useState, useEffect, useMemo } from 'react'
import { Head, usePage } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  LifeBuoy, Mail, Copy, Check, Clock, HelpCircle,
  ChevronDown, ChevronUp, Search, BookOpen, Wallet,
  ShieldAlert, ExternalLink
} from 'lucide-react'
import type { PageProps } from '@/types'

interface FAQItem {
  q: string
  a: string
  category: 'tests' | 'scoring' | 'wallet' | 'account'
}

const FAQ_CATEGORIES = [
  { id: 'all', label: 'All Questions', shortLabel: 'All' },
  { id: 'tests', label: 'Mock Tests & Exam Simulation', shortLabel: 'Mock Tests' },
  { id: 'scoring', label: 'Scoring & Solutions', shortLabel: 'Scoring' },
  { id: 'wallet', label: 'Credits & Wallet', shortLabel: 'Wallet' },
  { id: 'account', label: 'Account & Platform', shortLabel: 'Account' },
] as const

const FAQS: FAQItem[] = [
  // Tests & Simulation
  {
    category: 'tests',
    q: 'How do I unlock and attempt mock tests?',
    a: 'Each premium mock test requires a small number of credits (e.g., 5 credits). Click "Unlock" on any mock test card. If you have enough credits in your wallet, the test is instantly unlocked and ready to take. You can also claim daily free practice credits from your Wallet page.'
  },
  {
    category: 'tests',
    q: 'How does the test timer and auto-submission work?',
    a: 'Tests run on synchronized cloud countdowns according to the official exam blueprint (global timer or section-wise timers). When the timer reaches 00:00, your active responses are automatically submitted and graded immediately.'
  },
  {
    category: 'tests',
    q: 'Can I retake a mock test after submitting?',
    a: 'Yes! Once unlocked, mock tests can be retaken as many times as you like at no additional credit cost. You can also review comprehensive question-by-question analytics and step-by-step solutions for each attempt.'
  },
  {
    category: 'tests',
    q: 'Can I pause a test and resume it later?',
    a: 'Timed mock tests are designed to emulate real examination conditions, so the clock keeps ticking once started. However, you can freely navigate between allowed sections and questions before your time expires.'
  },
  {
    category: 'tests',
    q: 'What happens if my browser closes or internet disconnects?',
    a: 'PrepKiya auto-saves your marked answers in real time. If your tab accidentally closes or connection drops, simply reopen the test from your Student Dashboard before the timer expires, and you will resume right where you left off.'
  },

  // Scoring & Solutions
  {
    category: 'scoring',
    q: 'How is negative marking calculated?',
    a: 'Marking schemes strictly mirror the official examination rules. For example, in NISM Series V-A, each correct question awards +1 mark with 0 penalty, whereas banking or regulatory exams may apply a -0.25 negative marking deduction for incorrect choices. Unattempted questions carry zero penalty.'
  },
  {
    category: 'scoring',
    q: 'How are percentile and rank computed?',
    a: 'Your percentile and estimated rank are computed against all verified test attempts submitted across PrepKiya. It accurately indicates the percentage of candidates whose scores you surpassed.'
  },
  {
    category: 'scoring',
    q: 'Where can I find step-by-step solutions and explanations?',
    a: 'Immediately after submitting your test (or from "Attempts History" on your dashboard), click "View Solutions". Every question includes the correct answer, referenced regulations or formulas, and detailed analytical reasoning.'
  },
  {
    category: 'scoring',
    q: 'Can I bookmark tricky questions for quick revision?',
    a: 'Yes! During a test or while reviewing post-test solutions, click the Bookmark icon on any question. You can review and re-solve all your bookmarked questions anytime from the "Bookmarks" page in the student sidebar.'
  },

  // Wallet & Credits
  {
    category: 'wallet',
    q: 'How do practice credits work and how can I get more?',
    a: 'Credits allow you to unlock premium mock test series. You can claim daily check-in reward credits directly in your Wallet, earn milestone bonuses by maintaining practice streaks, or purchase credit packs.'
  },
  {
    category: 'wallet',
    q: 'Do my unlocked tests ever expire?',
    a: 'No. Once you unlock a mock test using your practice credits, it remains permanently available in your library for unlimited re-attempts and review.'
  },

  // Account & Platform
  {
    category: 'account',
    q: 'What should I do if I spot an error in a question?',
    a: 'During any test or while reviewing solutions, click the "Report" flag icon next to the question to submit instant feedback to our content audit team. Alternatively, you can email prepkiya@gmail.com with the Question ID.'
  },
  {
    category: 'account',
    q: 'How do I change the color theme (Dark / Light)?',
    a: 'Click the palette/theme menu icon in the navigation bar. PrepKiya supports multiple handcrafted themes including Dark Mode, Light Mode, and custom color accents designed for night studying without eye strain.'
  },
  {
    category: 'account',
    q: 'How can I request account deletion or data export?',
    a: 'In accordance with our Privacy Policy, you maintain complete ownership of your data. You can request account deletion or a data export anytime by emailing prepkiya@gmail.com from your registered email address.'
  }
]

const QUICK_INQUIRIES = [
  {
    title: 'Exam & Mock Inquiries',
    desc: 'Questions about syllabus, blueprint, or upcoming test releases.',
    subject: 'Exam & Mock Test Inquiry',
    icon: BookOpen,
  },
  {
    title: 'Wallet & Credits Support',
    desc: 'Issues with credit unlocks, transactions, or daily rewards.',
    subject: 'Wallet & Credits Assistance',
    icon: Wallet,
  },
  {
    title: 'Question Discrepancy',
    desc: 'Report an issue or suggest an improvement for a question.',
    subject: 'Question Discrepancy Feedback',
    icon: ShieldAlert,
  },
  {
    title: 'General Inquiries',
    desc: 'General platform feedback, partnerships, or suggestions.',
    subject: 'General PrepKiya Inquiry',
    icon: LifeBuoy,
  },
]

export default function HelpPage() {
  const { auth } = usePage<PageProps>().props
  usePublicPage()

  const [copied, setCopied] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  useEffect(() => {
    document.title = 'Help & Support — PrepKiya'
  }, [])

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('prepkiya@gmail.com')
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  const filteredFaqs = useMemo(() => {
    return FAQS.filter(faq => {
      const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory
      const matchesQuery = searchQuery.trim() === '' ||
        faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.a.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesCategory && matchesQuery
    })
  }, [selectedCategory, searchQuery])

  return (
    <>
      <Head title="Help & Support — PrepKiya">
        <meta
          name="description"
          content="Find answers to common questions about mock tests, scoring, and wallet credits, or contact PrepKiya support at prepkiya@gmail.com."
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-[#c8933b]/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 py-8 sm:py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-3.5 sm:px-6 lg:px-8 space-y-8 sm:space-y-12 sm:space-y-16">
            
            {/* Page Header */}
            <div className="text-center space-y-2.5 sm:space-y-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#f4f7fb] text-[#131b2e] border border-slate-200 shadow-2xs">
                <LifeBuoy className="h-3.5 w-3.5 text-[#c8933b]" />
                <span>Help & Support Desk</span>
              </div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-[#131b2e] tracking-tight">
                How Can We Help You?
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-slate-600 max-w-2xl mx-auto">
                Reach out directly to our dedicated academic & support team at <strong className="text-[#131b2e]">prepkiya@gmail.com</strong> or browse the answers below.
              </p>
            </div>

            {/* Official Email Banner Card */}
            <div className="rounded-2xl sm:rounded-3xl border border-[#c8933b]/30 bg-[#f8fafc] p-4 sm:p-6 lg:p-8 shadow-xs relative overflow-hidden">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 sm:gap-6">
                <div className="space-y-1.5 sm:space-y-2">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#b5802a] bg-[#c8933b]/15 px-2.5 py-0.5 sm:py-1 rounded-full border border-[#c8933b]/30 inline-flex items-center gap-1.5 shadow-2xs">
                    <Mail className="h-3 w-3" />
                    <span>Official Support Email</span>
                  </span>
                  <div>
                    <span className="text-xl sm:text-2xl lg:text-3xl font-black text-[#131b2e] tracking-tight font-mono break-all selection:bg-[#c8933b]/30">
                      prepkiya@gmail.com
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 flex items-center gap-1.5 pt-0.5">
                    <Clock className="h-3.5 w-3.5 text-[#c8933b] shrink-0" />
                    <span>Quick response: typically within 24 hours (Monday to Saturday).</span>
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full sm:w-auto pt-1 sm:pt-0">
                  <Button
                    type="button"
                    onClick={handleCopyEmail}
                    variant="outline"
                    className="h-10 px-4 rounded-xl text-xs font-bold border-slate-200 bg-white hover:bg-slate-50 text-slate-800 transition-all cursor-pointer w-full sm:w-auto justify-center"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1.5 text-emerald-600" />
                        <span>Copied to Clipboard!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1.5 text-slate-500" />
                        <span>Copy Email</span>
                      </>
                    )}
                  </Button>

                  <Button asChild className="h-10 px-5 rounded-xl text-xs font-bold bg-[#c8933b] hover:bg-[#b5802a] text-white shadow-xs transition-all w-full sm:w-auto justify-center">
                    <a href="mailto:prepkiya@gmail.com?subject=PrepKiya%20Support%20Request">
                      <Mail className="h-4 w-4 mr-1.5" />
                      <span>Open Mail App</span>
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Email Inquiry Shortcuts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-[#131b2e] flex items-center gap-2">
                  <Mail className="h-4.5 w-4.5 text-[#c8933b]" />
                  <span>Send Direct Email Inquiries</span>
                </h2>
                <span className="text-xs text-slate-500 hidden sm:inline">
                  Clicking opens your mail client pre-addressed to prepkiya@gmail.com
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {QUICK_INQUIRIES.map((inq) => {
                  const Icon = inq.icon
                  const mailtoUrl = `mailto:prepkiya@gmail.com?subject=${encodeURIComponent(inq.subject)}`
                  return (
                    <a
                      key={inq.title}
                      href={mailtoUrl}
                      className="p-5 rounded-2xl border border-slate-200 bg-white hover:border-[#c8933b]/50 hover:shadow-xs transition-all flex flex-col justify-between gap-3 group"
                    >
                      <div className="space-y-2">
                        <div className="w-9 h-9 rounded-xl bg-[#f4f7fb] text-[#131b2e] border border-slate-200 flex items-center justify-center transition-colors group-hover:bg-[#c8933b]/15 group-hover:text-[#c8933b] group-hover:border-[#c8933b]/30">
                          <Icon className="h-4 w-4" />
                        </div>
                        <h3 className="text-sm font-bold text-[#131b2e] group-hover:text-[#c8933b] transition-colors">
                          {inq.title}
                        </h3>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {inq.desc}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 text-[11px] font-bold text-[#c8933b] transition-colors pt-1">
                        <span>Write Email</span>
                        <ExternalLink className="h-3 w-3" />
                      </div>
                    </a>
                  )
                })}
              </div>
            </div>

            {/* Frequently Asked Questions Section */}
            <div className="space-y-4 sm:space-y-6 pt-2 sm:pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div className="space-y-1">
                  <h2 className="text-xl sm:text-2xl font-black text-[#131b2e] flex items-center gap-2.5">
                    <HelpCircle className="h-5 w-5 sm:h-6 sm:w-6 text-[#c8933b] shrink-0" />
                    <span>Frequently Asked Questions</span>
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Everything you need to know about tests, scoring, wallet, and account settings.
                  </p>
                </div>

                {/* Search Bar */}
                <div className="relative w-full sm:w-72">
                  <Search className="h-4 w-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <Input
                    type="text"
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 pl-9 rounded-xl bg-white border-slate-200 text-slate-900 text-xs focus-visible:ring-[#131b2e] w-full"
                  />
                </div>
              </div>

              {/* Category Filter Pills — Smooth scroll without native scrollbar */}
              <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1.5 no-scrollbar scroll-smooth">
                {FAQ_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(cat.id)
                      setOpenFaq(null)
                    }}
                    className={`px-3 sm:px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer shrink-0 ${
                      selectedCategory === cat.id
                        ? 'bg-[#131b2e] text-white shadow-2xs font-bold'
                        : 'bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <span className="sm:hidden">{cat.shortLabel}</span>
                    <span className="hidden sm:inline">{cat.label}</span>
                  </button>
                ))}
              </div>

              {/* FAQ Accordion List */}
              {filteredFaqs.length === 0 ? (
                <div className="p-6 sm:p-8 text-center rounded-2xl border border-slate-200 bg-white space-y-2">
                  <HelpCircle className="h-8 w-8 text-slate-400 mx-auto" />
                  <p className="text-sm font-semibold text-slate-900">No questions found</p>
                  <p className="text-xs text-slate-600">
                    Try searching for another keyword or email us directly at <strong className="text-slate-900">prepkiya@gmail.com</strong>.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5 sm:space-y-3">
                  {filteredFaqs.map((faq, index) => {
                    const isOpen = openFaq === index
                    return (
                      <div
                        key={index}
                        className="border border-slate-200 bg-white rounded-xl sm:rounded-2xl overflow-hidden transition-all shadow-2xs"
                      >
                        <button
                          type="button"
                          onClick={() => setOpenFaq(isOpen ? null : index)}
                          className="w-full p-3.5 sm:p-5 text-left flex items-center justify-between gap-3 text-xs sm:text-sm font-bold text-slate-900 hover:text-[#c8933b] transition-colors cursor-pointer"
                        >
                          <span className="leading-snug">{faq.q}</span>
                          {isOpen ? (
                            <ChevronUp className="h-4 w-4 text-[#c8933b] shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-3.5 sm:px-5 pb-3.5 sm:pb-5 pt-1 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                            {faq.a}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

          </div>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
